import React from 'react';
import { BrowserRouter as Router, Routes, Route } from 'react-router-dom';
import { AuthProvider } from './contexts/AuthContext';
import LoginRoute from './components/LoginRoute';
import HomeRedirect from './components/HomeRedirect';
import NotFoundPage from './pages/NotFoundPage';
import HomePage from './pages/HomePage';
import ProtectedRoute from './components/ProtectedRoute';
import ReportsPage from './pages/ReportsPage';
import SettingsPage from './pages/SettingsPage';
import ReportsOverviewPage from './pages/ReportsOverviewPage';
import HygienePage from './pages/HygienePage';
import FlowPage from './pages/FlowPage';
import EffortPage from './pages/EffortPage';
import PlanningPage from './pages/PlanningPage';
import PlanPage from './pages/PlanPage';
import ReportPage from './pages/ReportPage';
import MyPeoplePage from './pages/MyPeoplePage';
import CompliancePage from './pages/CompliancePage';
import MyAuditPage from './pages/MyAuditPage';
import GuidePage from './pages/GuidePage';
import ChangePasswordPage from './pages/ChangePasswordPage';
import { ToastProvider } from './contexts/ToastContext';
import { ConfirmProvider } from './contexts/ConfirmContext';
import ToastContainer from './components/ToastContainer';
import AppLayout from './components/AppLayout';
import { GoogleOAuthProvider } from '@react-oauth/google';

const GOOGLE_CLIENT_ID = process.env.REACT_APP_GOOGLE_CLIENT_ID || '';

const AppRoutes: React.FC = () => {
  return (
    <Routes>
      {/* The form when signed out; when signed in, the page they were heading for (?next=) or their home. */}
      <Route path="/login" element={<LoginRoute />} />
      {/* Everyone starts at Home (one short answer per role).
          It waits until the session has been read, so the role is known before anyone is sent anywhere. */}
      <Route path="/" element={<HomeRedirect />} />

      {/* Change-password lives outside AppLayout so it renders standalone
          (no sidebar) both when forced (must_change_password) and voluntary. */}
      <Route
        path="/change-password"
        element={<ProtectedRoute><ChangePasswordPage /></ProtectedRoute>}
      />

      <Route element={<ProtectedRoute><AppLayout /></ProtectedRoute>}>
        <Route path="/compliance" element={<CompliancePage />} />
        <Route path="/home" element={<HomePage />} />
        <Route path="/my-audit" element={<MyAuditPage />} />
        <Route path="/reports" element={<ReportsPage />} />
        <Route path="/reports-overview" element={<ReportsOverviewPage />} />
        <Route path="/hygiene" element={<HygienePage />} />
        <Route path="/flow" element={<FlowPage />} />
        <Route path="/effort" element={<EffortPage />} />
        <Route path="/planning" element={<PlanningPage />} />
        <Route path="/plan" element={<PlanPage />} />
        <Route path="/report" element={<ReportPage />} />
        <Route path="/my-people" element={<MyPeoplePage />} />
        <Route path="/settings" element={<SettingsPage />} />
        <Route path="/guide" element={<GuidePage />} />
        {/* Anything else: a signed-in person sees the pages they can open; a signed-out one is asked to sign in first. */}
        <Route path="*" element={<NotFoundPage />} />
      </Route>
    </Routes>
  );
};

const App: React.FC = () => {
  return (
    <GoogleOAuthProvider clientId={GOOGLE_CLIENT_ID}>
      <AuthProvider>
        <ToastProvider>
          <ConfirmProvider>
            <Router>
              <div className="App">
                <AppRoutes />
              </div>
            </Router>
          </ConfirmProvider>
          <ToastContainer />
        </ToastProvider>
      </AuthProvider>
    </GoogleOAuthProvider>
  );
};

export default App;
